import { useEffect, useMemo, useRef, useState } from "react";

/**
 * Interactive SVG candlestick chart with:
 *  - MA20 / MA50 overlays and pattern markers
 *  - Buy/sell markers, cost-basis line, P&L trail
 *  - Optional volume histogram panel underneath the price panel
 *  - Wheel-zoom and click-drag pan across the visible window
 *
 * candles: [{t, o, h, l, c, v?}]  (v = volume, optional)
 */
export default function CandlestickChart({
  candles = [],
  patterns = [],
  indicators = {},
  activeIndicators = ["sma20", "sma50"],
  markers = [],
  costBasis = null,
  pnlSegments = [],
  height = 320,
  showVolume = true,
  symbol = "",
  tradePlan = null,
}) {
  const [hover, setHover] = useState(null);
  const [markerHover, setMarkerHover] = useState(null);
  const svgRef = useRef(null);
  const drag = useRef(null);
  const touch = useRef(null);

  // Visible window (indices) — enables zoom/pan
  const [view, setView] = useState({ start: 0, end: Math.max(0, candles.length - 1) });

  // Draw tools state
  const [drawMode, setDrawMode] = useState(null); // null | 'trend' | 'hline'
  const [pendingAnchor, setPendingAnchor] = useState(null); // for trend line
  const drawKey = `chartDraws:${symbol || "generic"}`;
  const [draws, setDraws] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`chartDraws:${symbol || "generic"}`) || "[]"); } catch { return []; }
  });
  useEffect(() => {
    localStorage.setItem(drawKey, JSON.stringify(draws));
  }, [draws, drawKey]);
  useEffect(() => {
    try { setDraws(JSON.parse(localStorage.getItem(`chartDraws:${symbol || "generic"}`) || "[]")); }
    catch { setDraws([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  // Reset window when the candle set changes (e.g. new range)
  useEffect(() => {
    setView({ start: 0, end: Math.max(0, candles.length - 1) });
  }, [candles.length]);

  const hasVolume = useMemo(() => showVolume && candles.some((c) => c.v != null && c.v > 0), [candles, showVolume]);

  const { W, H, pad, priceH, volH } = useMemo(() => {
    const W = 900, H = height, pad = { t: 12, r: 44, b: 20, l: 10 };
    const availH = H - pad.t - pad.b;
    const vol = hasVolume ? Math.round(availH * 0.22) : 0;
    return { W, H, pad, priceH: availH - vol, volH: vol };
  }, [height, hasVolume]);

  if (!candles.length) return <div className="text-sm text-muted-foreground text-center py-10">No candles.</div>;

  // ---- Slice for the visible window
  const vStart = Math.max(0, Math.min(view.start, candles.length - 1));
  const vEnd = Math.max(vStart + 1, Math.min(view.end, candles.length - 1));
  const visible = candles.slice(vStart, vEnd + 1);
  const visIdxOf = (i) => i - vStart; // original -> visible index
  const inWindow = (i) => i >= vStart && i <= vEnd;

  const innerW = W - pad.l - pad.r;
  const highs = visible.map((c) => c.h), lows = visible.map((c) => c.l);
  const yMax = Math.max(...highs), yMin = Math.min(...lows);
  const yPad = (yMax - yMin) * 0.05 || 1;
  const yTop = yMax + yPad, yBot = yMin - yPad;
  const yScale = (v) => pad.t + ((yTop - v) / (yTop - yBot)) * priceH;

  const vols = visible.map((c) => c.v || 0);
  const vMax = Math.max(1, ...vols);
  const volTop = pad.t + priceH + 6;
  const vScale = (v) => volTop + (1 - v / vMax) * (volH - 6);

  const cw = innerW / visible.length;
  const bw = Math.max(1.5, cw * 0.7);

  // SMA overlays computed over the *full* candle series so early MA values are correct
  // but only rendered inside the window.
  const sma = (n) => {
    const out = new Array(candles.length).fill(null);
    let s = 0;
    for (let i = 0; i < candles.length; i++) {
      s += candles[i].c;
      if (i >= n) s -= candles[i - n].c;
      if (i >= n - 1) out[i] = s / n;
    }
    return out;
  };
  const ma20 = sma(20);
  const ma50 = sma(50);
  // Server-computed indicator series (aligned 1:1 with candles). Pulled from indicators.series.
  const s = indicators?.series || {};
  const ema20 = s.ema20 || [];
  const ema50 = s.ema50 || [];
  const bbUpper = s.bb_upper || [];
  const bbMid = s.bb_mid || [];
  const bbLower = s.bb_lower || [];
  const vwap = s.vwap || [];
  const fibLevels = indicators?.fib?.levels || null;
  const on = (k) => activeIndicators?.includes(k);
  const pathOf = (arr) => {
    let d = "";
    let first = true;
    for (let i = vStart; i <= vEnd; i++) {
      const v = arr[i];
      if (v == null) { first = true; continue; }
      const x = pad.l + visIdxOf(i) * cw + cw / 2;
      const y = yScale(v);
      d += `${first ? "M" : "L"} ${x} ${y} `;
      first = false;
    }
    return d.trim();
  };

  const patternByIdx = new Map();
  patterns.forEach((p) => {
    if (!patternByIdx.has(p.index)) patternByIdx.set(p.index, []);
    patternByIdx.get(p.index).push(p);
  });

  // ---- Zoom / Pan interactions
  const clampWindow = (s, e) => {
    const min = 8;
    const maxIdx = candles.length - 1;
    if (e - s < min) {
      if (s === 0) e = Math.min(maxIdx, s + min);
      else if (e === maxIdx) s = Math.max(0, e - min);
      else e = s + min;
    }
    s = Math.max(0, Math.min(maxIdx - min, Math.round(s)));
    e = Math.min(maxIdx, Math.max(s + min, Math.round(e)));
    return { start: s, end: e };
  };

  const onWheel = (evt) => {
    evt.preventDefault();
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const fx = (evt.clientX - rect.left) / rect.width;
    const s = vStart, e = vEnd;
    const size = e - s;
    // zoom in on wheel-up (deltaY<0), out on wheel-down
    const factor = evt.deltaY < 0 ? 0.85 : 1.18;
    const newSize = Math.max(8, Math.min(candles.length - 1, size * factor));
    const anchor = s + fx * size;
    const newStart = anchor - fx * newSize;
    const newEnd = newStart + newSize;
    setView(clampWindow(newStart, newEnd));
  };

  const onMouseDown = (evt) => {
    if (drawMode) return;  // clicks handled in onClick when drawing
    drag.current = { x: evt.clientX, start: vStart, end: vEnd };
  };
  const svgToDataCoords = (evt) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const sx = ((evt.clientX - rect.left) / rect.width) * W;
    const sy = ((evt.clientY - rect.top) / rect.height) * H;
    // Convert x to candle index (within window), then to time
    const relX = (sx - pad.l) / innerW;
    const idxInView = Math.max(0, Math.min(visible.length - 1, Math.round(relX * visible.length)));
    const t = visible[idxInView]?.t;
    // Convert y to price
    const priceRel = (sy - pad.t) / priceH;
    const price = yTop - priceRel * (yTop - yBot);
    return { t, price };
  };
  const onSvgClick = (evt) => {
    if (!drawMode) return;
    const pt = svgToDataCoords(evt);
    if (!pt) return;
    if (drawMode === "hline") {
      setDraws((cur) => [...cur, { kind: "hline", price: pt.price }]);
      setDrawMode(null);
    } else if (drawMode === "trend") {
      if (!pendingAnchor) {
        setPendingAnchor(pt);
      } else {
        setDraws((cur) => [...cur, { kind: "trend", a: pendingAnchor, b: pt }]);
        setPendingAnchor(null);
        setDrawMode(null);
      }
    }
  };
  const onMouseMove = (evt) => {
    if (!drag.current) return;
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const dxPct = (evt.clientX - drag.current.x) / rect.width;
    const size = drag.current.end - drag.current.start;
    const shift = -dxPct * size;
    setView(clampWindow(drag.current.start + shift, drag.current.end + shift));
  };
  const endDrag = () => { drag.current = null; };

  // Touch (mobile pan) — touch ref hoisted above the early return
  const onTouchStart = (e) => {
    if (e.touches.length === 1) touch.current = { x: e.touches[0].clientX, start: vStart, end: vEnd };
  };
  const onTouchMove = (e) => {
    if (!touch.current || e.touches.length !== 1) return;
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const dxPct = (e.touches[0].clientX - touch.current.x) / rect.width;
    const size = touch.current.end - touch.current.start;
    setView(clampWindow(touch.current.start - dxPct * size, touch.current.end - dxPct * size));
  };
  const onTouchEnd = () => { touch.current = null; };

  const resetView = () => setView({ start: 0, end: candles.length - 1 });
  const zoom = (factor) => {
    const size = vEnd - vStart;
    const mid = (vStart + vEnd) / 2;
    const newSize = Math.max(8, Math.min(candles.length - 1, size * factor));
    setView(clampWindow(mid - newSize / 2, mid + newSize / 2));
  };

  const fmtVol = (v) => v == null ? "" : v >= 1e9 ? `${(v / 1e9).toFixed(1)}B` : v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(0)}k` : String(Math.round(v));

  return (
    <div className="relative w-full select-none">
      {/* Toolbar */}
      <div className="absolute top-1 right-1 z-10 flex flex-wrap gap-1 opacity-90" data-testid="chart-controls">
        <button onClick={() => setDrawMode(drawMode === "trend" ? null : "trend")}
                data-testid="chart-draw-trend"
                title="Click two points to draw a trend line"
                className={`text-[10px] px-2 py-0.5 rounded border ${drawMode === "trend" ? "bg-[hsl(var(--ai))] text-black border-[hsl(var(--ai))]" : "border-border bg-card/80 hover:bg-secondary"}`}>Trend</button>
        <button onClick={() => setDrawMode(drawMode === "hline" ? null : "hline")}
                data-testid="chart-draw-hline"
                title="Click once to drop a horizontal support/resistance line"
                className={`text-[10px] px-2 py-0.5 rounded border ${drawMode === "hline" ? "bg-[hsl(var(--ai))] text-black border-[hsl(var(--ai))]" : "border-border bg-card/80 hover:bg-secondary"}`}>S/R</button>
        {draws.length > 0 && (
          <button onClick={() => { setDraws([]); setPendingAnchor(null); setDrawMode(null); }}
                  data-testid="chart-draw-clear"
                  className="text-[10px] px-2 py-0.5 rounded border border-border bg-card/80 hover:bg-secondary">Clear</button>
        )}
        <button onClick={() => zoom(0.7)} data-testid="chart-zoom-in"
                className="text-[10px] px-2 py-0.5 rounded border border-border bg-card/80 hover:bg-secondary">+</button>
        <button onClick={() => zoom(1.4)} data-testid="chart-zoom-out"
                className="text-[10px] px-2 py-0.5 rounded border border-border bg-card/80 hover:bg-secondary">−</button>
        <button onClick={resetView} data-testid="chart-reset-zoom"
                className="text-[10px] px-2 py-0.5 rounded border border-border bg-card/80 hover:bg-secondary">Reset</button>
        <button onClick={async () => {
                  const svg = svgRef.current;
                  if (!svg) return;
                  try {
                    // Inline current computed styles for offscreen serialization
                    const clone = svg.cloneNode(true);
                    // Ensure sizes for rasterization
                    const scale = 2;
                    clone.setAttribute("width", W * scale);
                    clone.setAttribute("height", H * scale);
                    // Add solid background so it looks right on light bg
                    const bg = document.createElementNS("http://www.w3.org/2000/svg", "rect");
                    bg.setAttribute("x", 0); bg.setAttribute("y", 0);
                    bg.setAttribute("width", W); bg.setAttribute("height", H);
                    // Use a dark bg similar to the app's card
                    bg.setAttribute("fill", getComputedStyle(document.documentElement).getPropertyValue("--card") ? `hsl(${getComputedStyle(document.documentElement).getPropertyValue("--card")})` : "#0b0b0b");
                    clone.insertBefore(bg, clone.firstChild);
                    // Watermark
                    const wm = document.createElementNS("http://www.w3.org/2000/svg", "text");
                    wm.textContent = "aanar · charted";
                    wm.setAttribute("x", W - 8);
                    wm.setAttribute("y", H - 4);
                    wm.setAttribute("text-anchor", "end");
                    wm.setAttribute("fill", "#666");
                    wm.setAttribute("font-size", "8");
                    wm.setAttribute("font-family", "JetBrains Mono, monospace");
                    clone.appendChild(wm);

                    const xml = new XMLSerializer().serializeToString(clone);
                    const svgBlob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
                    const url = URL.createObjectURL(svgBlob);
                    const img = new Image();
                    await new Promise((res, rej) => {
                      img.onload = res; img.onerror = rej; img.src = url;
                    });
                    const canvas = document.createElement("canvas");
                    canvas.width = W * scale; canvas.height = H * scale;
                    canvas.getContext("2d").drawImage(img, 0, 0);
                    URL.revokeObjectURL(url);
                    canvas.toBlob(async (blob) => {
                      if (!blob) return;
                      const filename = `aanar-chart-${Date.now()}.png`;
                      const file = new File([blob], filename, { type: "image/png" });
                      // Prefer native share on mobile
                      if (navigator.canShare && navigator.canShare({ files: [file] })) {
                        try {
                          await navigator.share({ files: [file], title: "My chart", text: "Check out this setup 📈" });
                          return;
                        } catch (_) { /* fall through to download */ }
                      }
                      const a = document.createElement("a");
                      a.href = URL.createObjectURL(blob);
                      a.download = filename;
                      a.click();
                      setTimeout(() => URL.revokeObjectURL(a.href), 500);
                    }, "image/png");
                  } catch (e) {
                    import("sonner").then(({ toast }) => toast.error("Screenshot failed"));
                  }
                }}
                data-testid="chart-share"
                title="Save or share this chart as a PNG"
                className="text-[10px] px-2 py-0.5 rounded border border-border bg-card/80 hover:bg-secondary">
          Share
        </button>
      </div>

      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-auto cursor-grab active:cursor-grabbing touch-pan-y"
        preserveAspectRatio="none"
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onClick={onSvgClick}
        onMouseMove={onMouseMove}
        style={{ cursor: drawMode ? "crosshair" : undefined }}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* User-drawn trend lines & support/resistance */}
        {draws.map((d, di) => {
          if (d.kind === "hline") {
            if (d.price < yBot || d.price > yTop) return null;
            const y = yScale(d.price);
            return (
              <g key={`draw-${di}`} data-testid={`draw-hline-${di}`}>
                <line x1={pad.l} x2={W - pad.r} y1={y} y2={y}
                      stroke="hsl(var(--ai))" strokeWidth="1.5" strokeDasharray="6 3" opacity="0.9" />
                <text x={pad.l + 4} y={y - 3} fill="hsl(var(--ai))" fontSize="9" fontFamily="JetBrains Mono">
                  ${d.price.toFixed(d.price > 100 ? 0 : 2)}
                </text>
              </g>
            );
          }
          // trend: two anchors {t, price}
          if (d.kind === "trend") {
            const iA = candles.findIndex((c) => c.t >= d.a.t);
            const iB = candles.findIndex((c) => c.t >= d.b.t);
            if (iA < 0 || iB < 0) return null;
            const xA = pad.l + (Math.max(0, iA - vStart) + 0.5) * cw;
            const xB = pad.l + (Math.max(0, iB - vStart) + 0.5) * cw;
            const yA = yScale(d.a.price);
            const yB = yScale(d.b.price);
            return (
              <g key={`draw-${di}`} data-testid={`draw-trend-${di}`}>
                <line x1={xA} y1={yA} x2={xB} y2={yB}
                      stroke="hsl(var(--ai))" strokeWidth="1.6" opacity="0.9" />
                <circle cx={xA} cy={yA} r="3" fill="hsl(var(--ai))" />
                <circle cx={xB} cy={yB} r="3" fill="hsl(var(--ai))" />
              </g>
            );
          }
          return null;
        })}
        {pendingAnchor && (
          <circle cx={pad.l + ((candles.findIndex((c) => c.t >= pendingAnchor.t) - vStart + 0.5) * cw)}
                  cy={yScale(pendingAnchor.price)} r="4" fill="hsl(var(--ai))" opacity="0.7"
                  data-testid="draw-pending-anchor" />
        )}

        {/* P&L Trail — coloured overlay between matched buy/sell fills */}
        {pnlSegments.map((seg, si) => {
          const i0 = candles.findIndex((c) => c.t >= seg.tStart);
          const i1 = candles.findIndex((c) => c.t >= seg.tEnd);
          if (i0 < 0 || i1 < 0 || i1 <= i0) return null;
          if (i1 < vStart || i0 > vEnd) return null;
          const s = Math.max(i0, vStart), e = Math.min(i1, vEnd);
          const x0 = pad.l + visIdxOf(s) * cw;
          const x1 = pad.l + (visIdxOf(e) + 1) * cw;
          return (
            <rect key={`pnl-${si}`} x={x0} y={pad.t} width={x1 - x0} height={priceH}
                  fill={seg.pnl >= 0 ? "hsl(var(--gain))" : "hsl(var(--loss))"} opacity="0.08" />
          );
        })}

        {/* Cost-basis dashed line */}
        {costBasis != null && costBasis >= yBot && costBasis <= yTop && (
          <g>
            <line x1={pad.l} x2={W - pad.r} y1={yScale(costBasis)} y2={yScale(costBasis)}
                  stroke="hsl(var(--ai))" strokeWidth="1.2" strokeDasharray="4 4" opacity="0.7" />
            <text x={W - pad.r - 4} y={yScale(costBasis) - 4} textAnchor="end"
                  fill="hsl(var(--ai))" fontSize="10" fontFamily="JetBrains Mono">
              avg ${costBasis.toFixed(costBasis > 100 ? 0 : 2)}
            </text>
          </g>
        )}

        {/* Grid */}
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={f} x1={pad.l} x2={W - pad.r} y1={pad.t + priceH * f} y2={pad.t + priceH * f}
                stroke="hsl(var(--border))" strokeDasharray="3 3" />
        ))}
        {/* Y-axis price ticks */}
        {[0, 0.25, 0.5, 0.75, 1].map((f) => {
          const v = yTop - f * (yTop - yBot);
          return <text key={f} x={W - pad.r + 4} y={pad.t + priceH * f + 3}
                       fill="hsl(var(--muted-foreground))" fontSize="10" fontFamily="JetBrains Mono">
            ${v.toFixed(v > 100 ? 0 : 2)}
          </text>;
        })}
        {/* MA overlays */}
        {on("sma20") && <path d={pathOf(ma20)} stroke="#F59E0B" strokeWidth="1.2" fill="none" opacity="0.85" />}
        {on("sma50") && <path d={pathOf(ma50)} stroke="#3B82F6" strokeWidth="1.2" fill="none" opacity="0.85" />}
        {on("ema20") && ema20.length > 0 && <path d={pathOf(ema20)} stroke="#EC4899" strokeWidth="1.2" fill="none" opacity="0.85" strokeDasharray="3 2" />}
        {on("ema50") && ema50.length > 0 && <path d={pathOf(ema50)} stroke="#10B981" strokeWidth="1.2" fill="none" opacity="0.85" strokeDasharray="3 2" />}
        {on("bb") && bbUpper.length > 0 && (
          <>
            <path d={pathOf(bbUpper)} stroke="#94A3B8" strokeWidth="1" fill="none" opacity="0.6" />
            <path d={pathOf(bbMid)} stroke="#94A3B8" strokeWidth="1" fill="none" opacity="0.4" strokeDasharray="2 2" />
            <path d={pathOf(bbLower)} stroke="#94A3B8" strokeWidth="1" fill="none" opacity="0.6" />
          </>
        )}
        {on("vwap") && vwap.length > 0 && <path d={pathOf(vwap)} stroke="#A855F7" strokeWidth="1.4" fill="none" opacity="0.9" />}
        {on("fib") && fibLevels && Object.entries(fibLevels).map(([k, v]) => {
          const y = yScale(v);
          if (Number.isNaN(y)) return null;
          return (
            <g key={`fib-${k}`}>
              <line x1={pad.l} y1={y} x2={pad.l + innerW} y2={y} stroke="#F59E0B" strokeWidth="0.75" strokeDasharray="4 3" opacity="0.5" />
              <text x={pad.l + innerW - 4} y={y - 2} textAnchor="end" fontSize="9" fill="#F59E0B" opacity="0.85">
                {k} · {v.toFixed(2)}
              </text>
            </g>
          );
        })}

        {/* Trade plan overlay: buy zone / stop zone / target line / A-B-C-D letters */}
        {tradePlan && tradePlan.entry != null && (() => {
          const p = tradePlan;
          const clamp = (v) => Math.max(pad.t, Math.min(pad.t + priceH, yScale(v)));
          const yEntry = clamp(p.entry);
          const yStop = clamp(p.stop);
          const yTarget = clamp(p.target);
          const isBull = p.kind !== "bear";
          const gain = "hsl(var(--gain))";
          const loss = "hsl(var(--loss))";
          const bandBuyTop = Math.min(yEntry, clamp(p.zones?.buy?.[1] ?? p.entry));
          const bandBuyBot = Math.max(yEntry, clamp(p.zones?.buy?.[0] ?? p.entry));
          const bandStopTop = Math.min(yStop, clamp(p.zones?.stop?.[1] ?? p.stop));
          const bandStopBot = Math.max(yStop, clamp(p.zones?.stop?.[0] ?? p.stop));
          return (
            <g data-testid="chart-trade-plan" pointerEvents="none">
              {/* Buy zone shaded band */}
              <rect x={pad.l} y={bandBuyTop}
                    width={W - pad.l - pad.r} height={Math.max(2, bandBuyBot - bandBuyTop)}
                    fill={gain} opacity="0.10" />
              <line x1={pad.l} x2={W - pad.r} y1={yEntry} y2={yEntry}
                    stroke={gain} strokeWidth="1.5" strokeDasharray="2 2" opacity="0.95" />
              <rect x={pad.l + 4} y={yEntry - 13} width={120} height={12}
                    fill="rgba(0,0,0,0.75)" stroke={gain} strokeWidth="0.75" rx="2" />
              <text x={pad.l + 10} y={yEntry - 4} fill={gain} fontSize="10" fontWeight="800"
                    fontFamily="JetBrains Mono">
                {`${isBull ? "BUY" : "SHORT"} $${p.entry.toFixed(2)}`}
              </text>
              {/* Stop zone shaded band */}
              <rect x={pad.l} y={bandStopTop}
                    width={W - pad.l - pad.r} height={Math.max(2, bandStopBot - bandStopTop)}
                    fill={loss} opacity="0.10" />
              <line x1={pad.l} x2={W - pad.r} y1={yStop} y2={yStop}
                    stroke={loss} strokeWidth="2" opacity="0.95" />
              <rect x={pad.l + 4} y={yStop + 2} width={120} height={12}
                    fill="rgba(0,0,0,0.75)" stroke={loss} strokeWidth="0.75" rx="2" />
              <text x={pad.l + 10} y={yStop + 11} fill={loss} fontSize="10" fontWeight="800"
                    fontFamily="JetBrains Mono">
                {`STOP $${p.stop.toFixed(2)}`}
              </text>
              {/* Target line */}
              <line x1={pad.l} x2={W - pad.r} y1={yTarget} y2={yTarget}
                    stroke="hsl(var(--ai))" strokeWidth="1.5" strokeDasharray="6 3" opacity="0.9" />
              <rect x={pad.l + 4} y={yTarget - 13} width={130} height={12}
                    fill="rgba(0,0,0,0.75)" stroke="hsl(var(--ai))" strokeWidth="0.75" rx="2" />
              <text x={pad.l + 10} y={yTarget - 4} fill="hsl(var(--ai))" fontSize="10" fontWeight="800"
                    fontFamily="JetBrains Mono">
                {`TARGET $${p.target.toFixed(2)}`}
              </text>
              {/* Swing point letters (A / B / C / D / X) */}
              {(p.points || []).map((pt, pi) => {
                if (pt.index == null || !inWindow(pt.index)) return null;
                const cx = pad.l + (visIdxOf(pt.index) + 0.5) * cw;
                const cy = clamp(pt.price);
                return (
                  <g key={`pt-${pi}`}>
                    <circle cx={cx} cy={cy} r="9" fill="hsl(var(--card))" stroke="hsl(var(--ai))" strokeWidth="1.5" />
                    <text x={cx} y={cy + 3.5} textAnchor="middle" fill="hsl(var(--ai))"
                          fontSize="10" fontWeight="800" fontFamily="Outfit, sans-serif">
                      {pt.label}
                    </text>
                  </g>
                );
              })}
              {/* Diagonal lines connecting swing points in order */}
              {(p.points || []).length >= 2 && (
                <polyline
                  points={(p.points || []).filter(pt => inWindow(pt.index))
                    .map(pt => `${pad.l + (visIdxOf(pt.index) + 0.5) * cw},${clamp(pt.price)}`).join(" ")}
                  fill="none" stroke="hsl(var(--ai))" strokeWidth="1.4"
                  strokeDasharray="4 3" opacity="0.55" />
              )}
            </g>
          );
        })()}

        {/* Candles + pattern markers */}
        {visible.map((c, i) => {
          const origIdx = vStart + i;
          const x = pad.l + i * cw + cw / 2;
          const isBull = c.c >= c.o;
          const color = isBull ? "hsl(var(--gain))" : "hsl(var(--loss))";
          const bodyTop = yScale(Math.max(c.o, c.c));
          const bodyBot = yScale(Math.min(c.o, c.c));
          const marks = patternByIdx.get(origIdx);
          return (
            <g key={origIdx} onMouseEnter={() => setHover({ c, i: origIdx, patterns: marks })}
               onMouseLeave={() => setHover(null)}>
              <line x1={x} x2={x} y1={yScale(c.h)} y2={yScale(c.l)} stroke={color} strokeWidth="1" />
              <rect x={x - bw/2} y={bodyTop} width={bw} height={Math.max(1, bodyBot - bodyTop)}
                    fill={color} stroke={color} />
              {marks && marks.length > 0 && (
                <g>
                  <circle cx={x} cy={yScale(c.h) - 8}
                          r="4" fill={marks[0].kind === "bull" ? "hsl(var(--gain))" : marks[0].kind === "bear" ? "hsl(var(--loss))" : "hsl(var(--ai))"} />
                  <text x={x} y={yScale(c.h) - 4} textAnchor="middle" fill="black" fontSize="7" fontWeight="700">
                    {marks[0].name[0]}
                  </text>
                </g>
              )}
            </g>
          );
        })}

        {/* Volume histogram panel */}
        {hasVolume && (
          <g data-testid="chart-volume">
            <line x1={pad.l} x2={W - pad.r} y1={volTop} y2={volTop} stroke="hsl(var(--border))" opacity="0.6" />
            {visible.map((c, i) => {
              const x = pad.l + i * cw + cw / 2;
              const isBull = c.c >= c.o;
              const color = isBull ? "hsl(var(--gain))" : "hsl(var(--loss))";
              const y = vScale(c.v || 0);
              return (
                <rect key={`v-${i}`} x={x - bw / 2} y={y}
                      width={bw} height={Math.max(0.5, volTop + volH - y)}
                      fill={color} opacity="0.55" />
              );
            })}
            <text x={W - pad.r + 4} y={volTop + 9} fill="hsl(var(--muted-foreground))" fontSize="9" fontFamily="JetBrains Mono">
              {fmtVol(vMax)}
            </text>
            <text x={W - pad.r + 4} y={volTop + volH - 2} fill="hsl(var(--muted-foreground))" fontSize="9" fontFamily="JetBrains Mono">
              0
            </text>
          </g>
        )}

        {/* Buy/Sell order markers */}
        {markers.map((m, mi) => {
          // Find closest candle within the visible window (fallback to nearest overall)
          let idx = 0, best = Infinity;
          for (let i = 0; i < candles.length; i++) {
            const d = Math.abs(candles[i].t - m.t);
            if (d < best) { best = d; idx = i; }
          }
          if (!inWindow(idx)) return null;
          const x = pad.l + visIdxOf(idx) * cw + cw / 2;
          const y = yScale(m.price);
          const isBuy = m.side === "buy";
          const color = isBuy ? "hsl(var(--gain))" : "hsl(var(--loss))";
          const yOff = isBuy ? 12 : -12;
          return (
            <g key={`m-${mi}`} data-testid={`marker-${m.id || mi}`}
               onMouseEnter={() => setMarkerHover({ m, x, y })}
               onMouseLeave={() => setMarkerHover(null)}
               style={{ cursor: "pointer" }}>
              <polygon
                points={isBuy
                  ? `${x - 6},${y + yOff + 9} ${x + 6},${y + yOff + 9} ${x},${y + yOff}`
                  : `${x - 6},${y + yOff - 9} ${x + 6},${y + yOff - 9} ${x},${y + yOff}`}
                fill={color} stroke={color} strokeWidth="1.5" />
              <line x1={x} y1={y} x2={x} y2={y + yOff + (isBuy ? 9 : -9)} stroke={color} strokeWidth="1" strokeDasharray="2 2" opacity="0.6" />
            </g>
          );
        })}
      </svg>

      {/* Legend + hover panel */}
      <div className="flex items-center gap-4 text-[11px] text-muted-foreground mt-2 flex-wrap font-mono-tab">
        <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-[#F59E0B]" /> SMA20</span>
        <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-[#3B82F6]" /> SMA50</span>
        <span className="flex items-center gap-1"><span className="text-[hsl(var(--gain))]">▲</span> Buys</span>
        <span className="flex items-center gap-1"><span className="text-[hsl(var(--loss))]">▼</span> Sells</span>
        {hasVolume && <span className="flex items-center gap-1"><span className="w-3 h-1 bg-foreground/60" /> Volume</span>}
        <span className="ml-auto text-[10px] uppercase">Scroll to zoom · drag to pan</span>
        {indicators.trend && <span>Trend: <span className={
          indicators.trend === "uptrend" ? "gain" : indicators.trend === "downtrend" ? "loss" : ""
        }>{indicators.trend}</span></span>}
        {indicators.rsi14 != null && <span>RSI14: <span className={
          indicators.rsi14 > 70 ? "loss" : indicators.rsi14 < 30 ? "gain" : ""
        }>{indicators.rsi14}</span></span>}
        {indicators.macd?.bias && <span>MACD: <span className={indicators.macd.bias === "bull" ? "gain" : "loss"}>{indicators.macd.bias}</span></span>}
      </div>

      {hover && (
        <div className="absolute top-2 left-2 bg-card border border-border rounded-lg px-3 py-1.5 text-[11px] font-mono-tab shadow-md pointer-events-none">
          <div>{new Date(hover.c.t).toLocaleDateString()}</div>
          <div>O ${hover.c.o.toFixed(2)} · H ${hover.c.h.toFixed(2)}</div>
          <div>L ${hover.c.l.toFixed(2)} · C ${hover.c.c.toFixed(2)}</div>
          {hover.c.v != null && <div className="text-muted-foreground">Vol {fmtVol(hover.c.v)}</div>}
          {hover.patterns?.map((p, k) => (
            <div key={k} className={p.kind === "bull" ? "gain" : p.kind === "bear" ? "loss" : "text-[hsl(var(--ai))]"}>{p.name}</div>
          ))}
        </div>
      )}
      {markerHover && (
        <div
          data-testid="marker-tooltip"
          className="absolute bg-card border border-border rounded-lg px-3 py-2 text-[11px] font-mono-tab shadow-md pointer-events-none z-10"
          style={{ left: `${(markerHover.x / W) * 100}%`, top: `${(markerHover.y / H) * 100 - 25}%` }}
        >
          <div className={`font-semibold uppercase ${markerHover.m.side === "buy" ? "gain" : "loss"}`}>
            {markerHover.m.side} · {markerHover.m.ticker || ""}
          </div>
          <div>Qty: {markerHover.m.quantity}</div>
          <div>Price: ${markerHover.m.price?.toFixed(2)}</div>
          {markerHover.m.total != null && <div>Total: ${markerHover.m.total.toFixed(2)}</div>}
          {markerHover.m.route && <div className="text-muted-foreground uppercase text-[10px]">{markerHover.m.route.replace("_", " ")}</div>}
          {markerHover.m.notes && <div className="text-muted-foreground italic mt-1 max-w-[200px] whitespace-normal">"{markerHover.m.notes}"</div>}
          <div className="text-muted-foreground text-[10px]">{new Date(markerHover.m.t).toLocaleString()}</div>
        </div>
      )}
    </div>
  );
}
